/**
 * A radiogroup of side-by-side cards, for the "pick one, and understand what
 * you picked" decisions a bare `<select>` under-explains.
 *
 * Lifted out of `features/customers` on its second usage (conventions rule 17):
 * the customer composer picks a billing mode with it, and the store's collection
 * form picks a line-sheet type. Feature folders can't import each other, and the
 * pattern was identical rather than merely similar.
 *
 * Fully controlled — owners of the value decide what a mode means downstream.
 */
import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "@flint/ui";

export interface ModeOption<TId extends string = string> {
  id: TId;
  /** Short, plain-voice title — e.g. `"Indian business"`. */
  title: string;
  /** One-liner under the title, explaining the mode in human terms. */
  sub: string;
  /**
   * Optional pill above the title — the customer composer uses it for the
   * field count (`"1 field"`), which is what kills its "13 required fields"
   * anxiety. Omit where there is nothing worth counting.
   */
  fieldsCount?: string;
  /** When true, paints the green "Recommended · ~80% of customers" tag. */
  recommended?: boolean;
  /** Optional override for the recommended tag. Default is the standard copy. */
  recommendedLabel?: string;
}

export interface ModeCardRowProps<TId extends string = string> {
  value: TId;
  onChange: (next: TId) => void;
  options: ReadonlyArray<ModeOption<TId>>;
  /** Optional className on the grid wrapper. */
  className?: string;
  /** Accessible label for the radiogroup. */
  ariaLabel?: string;
  /**
   * Renders the choice as settled and unchangeable — the cards stay visible so
   * the reader still sees what the alternative was, but nothing responds.
   * Pair it with `lockedHint` so the reason is readable, not just implied.
   */
  disabled?: boolean;
  /** Tooltip explaining why the choice is locked. */
  lockedHint?: string;
}

export function ModeCardRow<TId extends string>({
  value,
  onChange,
  options,
  className,
  ariaLabel = "Customer type",
  disabled = false,
  lockedHint,
}: ModeCardRowProps<TId>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      {...(disabled && lockedHint ? { title: lockedHint } : {})}
      className={cn(
        // Defaults to a 2-col mobile / 4-col desktop layout that accommodates
        // the 4 modes (B2B / B2C / Export / Tally). For 2- or 3-mode usages pass
        // a custom grid via `className`.
        "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5",
        className,
      )}
    >
      {options.map((opt) => (
        <ModeCard
          key={opt.id}
          option={opt}
          checked={opt.id === value}
          disabled={disabled}
          {...(lockedHint ? { lockedHint } : {})}
          onSelect={() => onChange(opt.id)}
        />
      ))}
    </div>
  );
}

interface ModeCardProps<TId extends string> {
  option: ModeOption<TId>;
  checked: boolean;
  disabled: boolean;
  lockedHint?: string;
  onSelect: () => void;
}

function ModeCard<TId extends string>({
  option,
  checked,
  disabled,
  lockedHint,
  onSelect,
}: ModeCardProps<TId>) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      onSelect();
    }
  };
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      disabled={disabled}
      {...(disabled && lockedHint ? { title: lockedHint } : {})}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={handleKeyDown}
      className={cn(
        "relative min-h-[96px] text-left",
        "flex flex-col gap-1 p-3.5 pt-3 pr-3",
        "rounded-md border-[1.5px] bg-paper",
        "transition-all duration-100",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200",
        disabled ? "cursor-default" : "cursor-pointer",
        // A locked choice reads as settled, not as an error or a live control:
        // the picked card keeps its shape but drops the sky wash, and the one
        // not taken fades back.
        disabled
          ? checked
            ? "border-line-2 bg-raised"
            : "border-line opacity-60"
          : checked
            ? "border-sky-600 bg-sky-50 ring-[3px] ring-sky-100"
            : "border-line hover:border-line-2 hover:bg-raised",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "absolute top-2.5 right-2.5 grid place-items-center w-[18px] h-[18px] rounded-full",
          "transition-colors duration-100",
          checked
            ? disabled
              ? "bg-line-2 border-[1.5px] border-line-2 text-paper"
              : "bg-sky-600 border-[1.5px] border-sky-600 text-paper"
            : "bg-paper border-[1.5px] border-dashed border-line-2 text-transparent",
        )}
      >
        <Check strokeWidth={3} className="w-2.5 h-2.5" />
      </span>

      {option.fieldsCount ? (
        <span
          className={cn(
            "self-start font-mono text-xs font-semibold leading-none px-[7px] py-[2px] rounded-full -tracking-[0.01em]",
            checked && !disabled ? "bg-sky-600 text-paper" : "bg-raised text-muted",
          )}
        >
          {option.fieldsCount}
        </span>
      ) : null}

      <strong className="text-sm font-semibold text-ink -tracking-[0.005em]">
        {option.title}
      </strong>
      <span className="text-xs text-muted leading-snug">{option.sub}</span>

      {option.recommended ? (
        <span className="mt-1.5 self-start text-xs font-semibold text-sky-700 bg-sky-100 px-[7px] py-[2px] rounded-full">
          {option.recommendedLabel ?? "Recommended · ~80% of customers"}
        </span>
      ) : null}
    </button>
  );
}
